import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import { Card, CardContent } from '../../../../components/ui/Card';
import { releaseWpsHold, releaseWpsLine } from '../../../../api/Payroll/wps';
import {
  WpsCoverageEmployeeView,
  WpsCoverageView,
  WpsEmployeeClass,
  WpsLineReleaseReason,
} from '../../../../models/payroll';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import { HoldBackForm } from './HoldBackForm';
import { InlineConfirm } from './InlineConfirm';
import { PayrollTableLink } from './PayrollTableLink';
import { useWpsConfirm } from './useWpsConfirm';
import { useWpsLabels } from './wpsLabels';

const LINE_REASONS: WpsLineReleaseReason[] = ['BankRejectedLine', 'BankReturnedMoney', 'FileNotSent', 'Other'];
const GROUP_PREVIEW = 20;

interface TakeOutProps {
  person: WpsCoverageEmployeeView;
  onDone: () => void;
  onCancel: () => void;
}

/** The one-employee correction. The warning comes from the server once a reason is chosen, naming this person. */
const TakeOutOfFile: React.FC<TakeOutProps> = ({ person, onDone, onCancel }) => {
  const { t } = useTranslation();
  const labels = useWpsLabels();
  const [reason, setReason] = useState<WpsLineReleaseReason | ''>('');
  const [note, setNote] = useState('');
  const finished = useCallback(() => onDone(), [onDone]);
  const { warnings, error, busy, run, reset } = useWpsConfirm(finished);

  const lineId = person.lineId ?? 0;
  const noteNeeded = reason === 'Other';
  const ready = reason !== '' && (!noteNeeded || note.trim().length > 0);

  const preview = (nextReason: WpsLineReleaseReason, nextNote: string) => {
    reset();
    if (nextReason === 'Other' && !nextNote.trim()) return;
    void run(() => releaseWpsLine(lineId, { reason: nextReason, note: nextReason === 'Other' ? nextNote.trim() : undefined, confirm: false }));
  };

  return (
    <InlineConfirm
      warnings={warnings}
      error={error}
      busy={busy}
      destructive
      confirmLabel={t('payroll.wps.uae.takeOut.confirm', { name: person.employeeName })}
      canConfirm={ready && warnings !== null}
      onConfirm={() => {
        if (reason === '') return;
        void run(() => releaseWpsLine(lineId, { reason, note: noteNeeded ? note.trim() : undefined, confirm: true }));
      }}
      onCancel={onCancel}
      testId="wps-take-out"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label={t('payroll.wps.uae.takeOut.reason')}
          placeholder={t('payroll.wps.uae.hold.reasonPlaceholder')}
          value={reason}
          options={LINE_REASONS.map(value => ({ value, label: labels.lineReason(value) }))}
          onChange={(value: WpsLineReleaseReason) => {
            setReason(value);
            preview(value, note);
          }}
          data-testid="wps-take-out-reason"
        />
        {noteNeeded && (
          <Input
            label={t('payroll.wps.uae.note')}
            value={note}
            maxLength={500}
            required
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
            onBlur={() => {
              preview('Other', note);
            }}
            data-testid="wps-take-out-note"
          />
        )}
      </div>
      <p className="text-xs text-muted-foreground">{t('payroll.wps.uae.takeOut.whichToUse')}</p>
    </InlineConfirm>
  );
};

interface PersonRowProps {
  year: number;
  month: number;
  person: WpsCoverageEmployeeView;
  holdBackEnabled: boolean;
  onChanged: () => void;
}

const PersonRow: React.FC<PersonRowProps> = ({ year, month, person, holdBackEnabled, onChanged }) => {
  const { t } = useTranslation();
  const labels = useWpsLabels();
  const [open, setOpen] = useState<'takeOut' | 'change' | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const done = () => {
    setOpen(null);
    onChanged();
  };

  const release = async () => {
    setReleasing(true);
    setError(null);
    try {
      const result = await releaseWpsHold(year, month, person.userId);
      if (result.errors.length > 0) setError(getApiErrorMessage(result, t));
      else onChanged();
    } catch (e) {
      setError(getApiErrorMessage(e, t));
    } finally {
      setReleasing(false);
    }
  };

  const reasonText = (() => {
    if (person.state === 'Held' || person.state === 'Closed') return labels.holdReason(person.holdReason);
    if (person.state === 'Outside') return labels.outsideReason(person.outsideReason);
    return '';
  })();
  const noteText = person.state === 'Outside' ? person.outsideNote : person.holdNote;
  const isHold = person.state === 'Held' || person.state === 'Closed';

  return (
    <li className="py-2" data-testid="wps-person-row" data-employee={person.employeeName}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div className="min-w-0 text-sm">
          <span className="font-medium text-foreground">{person.employeeName}</span>
          {person.staffNumber && <span className="ms-2 text-xs text-muted-foreground">{person.staffNumber}</span>}
          {reasonText && <span className="ms-2 text-muted-foreground">{reasonText}</span>}
          {noteText && <p className="text-xs text-muted-foreground">{noteText}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {person.lineId != null && open === null && (
            <Button size="xs" variant="ghost" onClick={() => setOpen('takeOut')} data-testid="wps-take-out-open">
              {t('payroll.wps.uae.takeOut.action')}
            </Button>
          )}
          {isHold && holdBackEnabled && open === null && (
            <Button size="xs" variant="ghost" onClick={() => setOpen('change')}>
              {t('payroll.wps.uae.hold.changeReason')}
            </Button>
          )}
          {isHold && open === null && (
            <Button size="xs" variant="ghost" onClick={release} loading={releasing} disabled={releasing} data-testid="wps-hold-release">
              {t('payroll.wps.uae.hold.release')}
            </Button>
          )}
        </div>
      </div>
      {error && <p className="mt-1 text-sm text-red-700 dark:text-red-300" role="alert">{error}</p>}
      {open === 'takeOut' && <TakeOutOfFile person={person} onDone={done} onCancel={() => setOpen(null)} />}
      {open === 'change' && (
        <HoldBackForm
          year={year}
          month={month}
          userId={person.userId}
          employeeName={person.employeeName}
          initialReason={person.holdReason}
          initialNote={person.holdNote}
          onDone={done}
          onCancel={() => setOpen(null)}
        />
      )}
    </li>
  );
};

interface PeoplePanelProps {
  year: number;
  month: number;
  coverage: WpsCoverageView | null;
  holdBackEnabled: boolean;
  /** Warn only: people with no payroll row are a named warning that does not hold the month open. */
  noPayrollWarnOnly: boolean;
  /** Held by the page, because the file panel can open a group from its warning. */
  expanded: WpsEmployeeClass | null;
  onExpandedChange: (state: WpsEmployeeClass | null) => void;
  onChanged: () => void;
}

/** Who is where this month. Every group starts collapsed, so a long list never buries the actions above it. */
export const PeoplePanel: React.FC<PeoplePanelProps> = ({
  year,
  month,
  coverage,
  holdBackEnabled,
  noPayrollWarnOnly,
  expanded,
  onExpandedChange,
  onChanged,
}) => {
  const { t } = useTranslation();
  const labels = useWpsLabels();
  const [showAll, setShowAll] = useState(false);

  const byState = useMemo(() => {
    const map = new Map<WpsEmployeeClass, WpsCoverageEmployeeView[]>();
    (coverage?.items ?? []).forEach(person => {
      map.set(person.state, [...(map.get(person.state) ?? []), person]);
    });
    return map;
  }, [coverage]);

  if (!coverage) return null;

  const groups: { state: WpsEmployeeClass; title: string; warn?: boolean }[] = [
    { state: 'ToFile', title: t('payroll.wps.uae.people.toFile') },
    { state: 'InFile', title: t('payroll.wps.uae.people.inFile') },
    { state: 'Submitted', title: t('payroll.wps.uae.people.submitted') },
    { state: 'Held', title: t('payroll.wps.uae.people.held') },
    { state: 'Closed', title: t('payroll.wps.uae.people.closed') },
    { state: 'Outside', title: t('payroll.wps.uae.people.outside') },
    { state: 'NoPayroll', title: t('payroll.wps.uae.people.noPayroll'), warn: true },
  ];
  const visible = groups.filter(g => (byState.get(g.state)?.length ?? 0) > 0);
  if (visible.length === 0 && coverage.archivedExcludedCount === 0 && coverage.differing.length === 0) return null;

  const differingBy = (reason: string) => coverage.differing.filter(d => d.reason === reason).map(d => d.employeeName);
  const amountChanged = differingBy('amountChanged');
  const currencyChanged = differingBy('currencyChanged');
  const rowArchived = differingBy('rowArchived');
  const differLines = [
    amountChanged.length > 0 && t('payroll.wps.uae.differs.amountChanged', { names: labels.cappedNames(amountChanged) }),
    currencyChanged.length > 0 && t('payroll.wps.uae.differs.currencyChanged', { names: labels.cappedNames(currencyChanged) }),
    rowArchived.length > 0 && t('payroll.wps.uae.differs.rowArchived', { names: labels.cappedNames(rowArchived) }),
  ].filter(Boolean) as string[];

  return (
    <Card id="wps-people" className="scroll-mt-24">
      <CardContent className="p-4" data-testid="wps-people-panel">
        <h3 className="font-semibold mb-1">{t('payroll.wps.uae.people.title')}</h3>

        {differLines.length > 0 && (
          <ul className="my-2 space-y-1.5" data-testid="wps-differs">
            {differLines.map(line => (
              <li key={line} className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        )}

        {noPayrollWarnOnly && (byState.get('NoPayroll')?.length ?? 0) > 0 && (
          <p className="my-2 flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200" data-testid="wps-no-payroll-warning">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
            <span>
              {t('payroll.wps.uae.people.noPayrollWarn', {
                names: labels.cappedNames((byState.get('NoPayroll') ?? []).map(p => p.employeeName)),
              })}{' '}
              <PayrollTableLink />
            </span>
          </p>
        )}

        <ul className="divide-y divide-border">
          {visible.map(group => {
            const people = byState.get(group.state) ?? [];
            const isOpen = expanded === group.state;
            const shown = isOpen && !showAll ? people.slice(0, GROUP_PREVIEW) : people;
            return (
              <li key={group.state} data-testid={`wps-people-group-${group.state}`}>
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-3 py-2.5 text-start text-sm"
                  aria-expanded={isOpen}
                  onClick={() => {
                    onExpandedChange(isOpen ? null : group.state);
                    setShowAll(false);
                  }}
                >
                  <span className={`font-medium ${group.warn ? 'text-amber-800 dark:text-amber-200' : 'text-foreground'}`}>
                    {t('payroll.wps.uae.people.groupCount', { group: group.title, n: people.length })}
                  </span>
                  <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
                {isOpen && (
                  <div className="pb-2">
                    {group.state === 'NoPayroll' && (
                      <p className="pb-1 text-sm" data-testid="wps-no-payroll-group-link"><PayrollTableLink /></p>
                    )}
                    <ul className="divide-y divide-border/60">
                      {shown.map(person => (
                        <PersonRow
                          key={person.userId}
                          year={year}
                          month={month}
                          person={person}
                          holdBackEnabled={holdBackEnabled}
                          onChanged={onChanged}
                        />
                      ))}
                    </ul>
                    {people.length > shown.length && (
                      <button
                        type="button"
                        className="mt-1 text-sm font-medium text-primary hover:underline"
                        onClick={() => setShowAll(true)}
                      >
                        {t('payroll.wps.uae.people.showAll', { more: people.length - shown.length })}
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {coverage.archivedExcludedCount > 0 && (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="wps-archived-excluded">
            {t('payroll.wps.uae.people.archived', { n: coverage.archivedExcludedCount })}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
